import { Component } from '@angular/core';
import { BaseCellComponent, TableRowButtonsComponent, TableRowButton } from '@adnova/jf-ng-components';

import { AnnouncementTableItem } from '../+definitions';

/**
 * Name des CustomEvents für Cell-Aktionen.
 *
 * Wird verwendet, um Aktionen an die Parent-Komponente zu kommunizieren.
 */
const CELL_ACTION_EVENT = 'cellAction';

/**
 * Typen von Aktionen, die von dieser Cell ausgelöst werden können.
 */
export type CellActionType = 'view' | 'edit' | 'delete';

/**
 * Interface für das Detail-Objekt des CustomEvents.
 */
export interface CellActionDetail {
  /** Die Art der Aktion */
  action: CellActionType;
  /** Das betroffene Announcement */
  item: AnnouncementTableItem;
}

/**
 * Cell-Komponente für die Aktionen-Spalte in der Announcement-Tabelle.
 *
 * Verwendet die `jf-table-row-actions` Komponente aus der jf-lib für konsistentes
 * Styling und Verhalten.
 *
 * Bietet folgende Aktionen:
 * - **Vorschau**: Öffnet die Detailansicht des Announcements (Primary Button)
 * - **Bearbeiten**: Öffnet den Editor im Edit-Modus (Secondary Button)
 * - **Löschen**: Öffnet den Lösch-Dialog (Tertiary Button)
 *
 * Die Aktionen werden über CustomEvents (`cellAction`) an die Parent-Komponente
 * kommuniziert. Das Event enthält die Aktion und das betroffene Item.
 *
 * @extends BaseCellComponent<AnnouncementTableItem>
 *
 * @example
 * ```typescript
 * // In der Parent-Komponente:
 * document.addEventListener('cellAction', (event: CustomEvent<CellActionDetail>) => {
 *   const { action, item } = event.detail;
 *   switch (action) {
 *     case 'view': this.viewAnnouncement(item); break;
 *     case 'edit': this.editAnnouncement(item); break;
 *     case 'delete': this.deleteAnnouncement(item); break;
 *   }
 * });
 * ```
 */
@Component({
  selector: 'app-actions-cell',
  standalone: true,
  imports: [TableRowButtonsComponent],
  templateUrl: './actions-cell.component.html',
})
export class ActionsCellComponent extends BaseCellComponent<AnnouncementTableItem> {

  /**
   * Primary Button für Vorschau/Detailansicht.
   *
   * Leitet zur Vorschau des Announcements weiter.
   */
  readonly viewButton: TableRowButton = {
    iconName: 'eye',
    tooltip: 'Vorschau',
    action: () => this.dispatchAction('view'),
  };

  /**
   * Secondary Button für Bearbeiten.
   *
   * Öffnet den Editor im Edit-Modus für das Announcement.
   */
  readonly editButton: TableRowButton = {
    iconName: 'edit',
    tooltip: 'Bearbeiten',
    action: () => this.dispatchAction('edit'),
  };

  /**
   * Tertiary Button für Löschen.
   */
  readonly deleteButton: TableRowButton = {
    iconName: 'basket',
    tooltip: 'Löschen',
    action: () => this.dispatchAction('delete'),
  };

  /**
   * Dispatcht ein CustomEvent mit der Action und dem aktuellen Item.
   *
   * Das Event wird auf `document` dispatcht, damit die Parent-Komponente
   * es empfangen kann, ohne direkten Zugriff auf diese Cell zu haben.
   *
   * @param action - Der Name der Aktion
   */
  private dispatchAction(action: CellActionType): void {
    const item = this.data();
    if (!item) return;

    const detail: CellActionDetail = { action, item };
    document.dispatchEvent(new CustomEvent(CELL_ACTION_EVENT, { detail }));
  }
}
